// Safe-debug bundle builder.
//
// Single helper that both the Login screen and the AuthRequired
// guard call to produce the "Copy safe auth debug" clipboard
// payload. The bundle deliberately omits the bearer token, raw
// signature, HMAC secret, and any other sensitive material — it is
// designed to be shareable in a support thread / GitHub issue
// without escalating its sensitivity.
//
// The shape is documented in the operator brief §G; this file is
// the single source of truth for what gets exported.

import { API_BASE } from "../config";
import { BUILD_ID, COMMIT_SHA, BUILD_TIME } from "../buildInfo";
import {
  getLast401Route,
  getSessionClearReason,
  getSessionMetadata,
  getSessionToken,
  hasSessionStorageRow,
} from "./session";

export interface LoginDebugInput {
  /** The phase machine state from the Login screen. `null` for
   *  AuthRequired (which has no challenge/verify state of its own). */
  phase: string | null;
  walletProviderDetected: boolean | null;
  walletAddress: string | null;
  walletChainId: number | null;
  expectedChainId: number | null;
  ownerSubject: string | null;
  ownerSubjectLength: number | null;
  challengeHttpStatus: number | null;
  challengeOk: boolean | null;
  challengeError: string | null;
  verifyHttpStatus: number | null;
  verifyOk: boolean | null;
  verifyError: string | null;
  sessionProbeHttpStatus: number | null;
  sessionProbeOk: boolean | null;
  sessionProbeError: string | null;
  beErrorCode: string | null;
  beErrorMessage: string | null;
  // EIP-1193 rejection detail. Present ONLY when the wallet refused, and the
  // reason the bundle exists at all: a provider rejection is the one failure the
  // report used to capture nothing about, because `lastExceptionMessage` is
  // null for a non-`Error` throw and the banner had already flattened the
  // object to "[object Object]".
  providerErrorCode: number | null;
  providerErrorMessage: string | null;
  providerErrorData: string | null;
  providerErrorKind: string | null;
  signError: string | null;
}

export interface SafeDebugBundle {
  buildId: string;
  commitSha: string;
  buildTime: string;
  apiBase: string;
  currentRoute: string;
  returnTo: string | null;

  // Login-input state (null on AuthRequired).
  loginPhase: string | null;
  walletProviderDetected: boolean | null;
  walletAddress: string | null;
  walletChainId: number | null;
  expectedChainId: number | null;
  ownerSubject: string | null;
  ownerSubjectLength: number | null;
  challengeHttpStatus: number | null;
  challengeOk: boolean | null;
  challengeError: string | null;
  verifyHttpStatus: number | null;
  verifyOk: boolean | null;
  verifyError: string | null;
  sessionProbeHttpStatus: number | null;
  sessionProbeOk: boolean | null;
  sessionProbeError: string | null;
  beErrorCode: string | null;
  beErrorMessage: string | null;
  // EIP-1193 rejection detail. Present ONLY when the wallet refused, and the
  // reason the bundle exists at all: a provider rejection is the one failure the
  // report used to capture nothing about, because `lastExceptionMessage` is
  // null for a non-`Error` throw and the banner had already flattened the
  // object to "[object Object]".
  providerErrorCode: number | null;
  providerErrorMessage: string | null;
  providerErrorData: string | null;
  providerErrorKind: string | null;
  signError: string | null;

  // Session-store snapshot (live read at copy time).
  sessionTokenPresent: boolean;
  sessionStorageRowExists: boolean;
  restoredSessionExists: boolean;
  sessionMetadata: {
    ownerSubject: string;
    walletAddress: string;
  } | null;
  last401Route: string | null;
  sessionClearReason: string | null;
}

export function buildSafeDebugBundle(args: {
  route: string;
  returnTo: string | null;
  login?: Partial<LoginDebugInput>;
}): SafeDebugBundle {
  const md = getSessionMetadata();
  const login: Partial<LoginDebugInput> = args.login ?? {};
  return {
    buildId: BUILD_ID,
    commitSha: COMMIT_SHA,
    buildTime: BUILD_TIME,
    apiBase: API_BASE,
    currentRoute: args.route,
    returnTo: args.returnTo,

    loginPhase: login.phase ?? null,
    walletProviderDetected: login.walletProviderDetected ?? null,
    walletAddress: login.walletAddress ?? null,
    walletChainId: login.walletChainId ?? null,
    expectedChainId: login.expectedChainId ?? null,
    ownerSubject: login.ownerSubject ?? null,
    ownerSubjectLength: login.ownerSubjectLength ?? null,
    challengeHttpStatus: login.challengeHttpStatus ?? null,
    challengeOk: login.challengeOk ?? null,
    challengeError: login.challengeError ?? null,
    verifyHttpStatus: login.verifyHttpStatus ?? null,
    verifyOk: login.verifyOk ?? null,
    verifyError: login.verifyError ?? null,
    sessionProbeHttpStatus: login.sessionProbeHttpStatus ?? null,
    sessionProbeOk: login.sessionProbeOk ?? null,
    sessionProbeError: login.sessionProbeError ?? null,
    beErrorCode: login.beErrorCode ?? null,
    beErrorMessage: login.beErrorMessage ?? null,
    providerErrorCode: login.providerErrorCode ?? null,
    providerErrorMessage: login.providerErrorMessage ?? null,
    providerErrorData: login.providerErrorData ?? null,
    providerErrorKind: login.providerErrorKind ?? null,
    signError: login.signError ?? null,

    // NEVER reads the bearer token — only its presence.
    sessionTokenPresent: getSessionToken() !== null,
    sessionStorageRowExists: hasSessionStorageRow(),
    restoredSessionExists: md !== null,
    sessionMetadata:
      md === null
        ? null
        : {
            ownerSubject: md.ownerSubject,
            walletAddress: md.walletAddress,
          },
    last401Route: getLast401Route(),
    sessionClearReason: getSessionClearReason(),
  };
}

export function copySafeDebugToClipboard(bundle: SafeDebugBundle): void {
  const text = JSON.stringify(bundle, null, 2);
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      navigator.clipboard.writeText
    ) {
      void navigator.clipboard.writeText(text);
    }
  } catch {
    // Defensive — some webviews lack clipboard API.
  }
}
